import React, { useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './DynamicHeader.scss';

import backIcon from './img/back.png';
import moreIcon from './img/moreIcon.png';

export default function DynamicHeader({
  title = '',
  variant = 'solid',
  size = 'compact',
  hideOnScroll = true,
  showBack = true,
  onBack,
  showMenu = false,
  onMenuClick,
  rightNode = null,
  withSpacer = false,
  // Optional custom center content (e.g. a compact segmented control),
  // rendered instead of the plain <h1>{title}</h1>. Purely additive — every
  // existing caller that doesn't pass this keeps the exact same title
  // rendering as before.
  centerNode = null,
  // Optional: pad the fixed bar down by env(safe-area-inset-top) instead of
  // sitting flush at the viewport's physical top edge (0), so back/center/
  // right controls don't crowd a notch/Dynamic Island. Defaults to false —
  // every existing caller keeps today's exact positioning; this is opt-in
  // per page, not a global behavior change.
  respectSafeAreaTop = false,
}) {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);

  const lastYRef = useRef(0);
  const tickingRef = useRef(false);

  const height = size === 'regular' ? 56 : 48;

  useLayoutEffect(() => {
    document.body.dataset.localHeader = 'true';
    document.body.dataset.localHeaderHeight = String(height);
    document.body.dataset.localHeaderVariant = variant;

    return () => {
      if (document.body.dataset.localHeader === 'true') {
        delete document.body.dataset.localHeader;
        delete document.body.dataset.localHeaderHeight;
        delete document.body.dataset.localHeaderVariant;
      }
    };
  }, [height, variant]);

  const goBack = () => {
    if (typeof onBack === 'function') onBack();
    else navigate(-1);
  };

  const onScroll = useCallback(() => {
    if (!hideOnScroll) return;

    const y = window.scrollY || 0;
    const last = lastYRef.current;
    const delta = y - last;

    if (y < 6 || delta < -2) {
      if (hidden) setHidden(false);
    } else if (delta > 2 && y > height) {
      if (!hidden) setHidden(true);
    }

    lastYRef.current = y;
    tickingRef.current = false;
  }, [hideOnScroll, hidden, height]);

  React.useEffect(() => {
    if (!hideOnScroll) return;

    const handler = () => {
      if (tickingRef.current) return;

      tickingRef.current = true;
      window.requestAnimationFrame(onScroll);
    };

    lastYRef.current = window.scrollY || 0;

    window.addEventListener('scroll', handler, { passive: true });

    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, [hideOnScroll, onScroll]);

  const cls = [
    'dh',
    'dh--fixed',
    size === 'regular' ? 'dh--regular' : 'dh--compact',
    variant === 'glass'
      ? 'dh--glass'
      : variant === 'solid'
        ? 'dh--solid'
        : 'dh--transparent',
    hideOnScroll ? 'dh--autohide' : '',
    hidden ? 'dh--hidden' : '',
    respectSafeAreaTop ? 'dh--safe-top' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <header
        className={cls}
        role="banner"
        aria-hidden={hidden ? 'true' : 'false'}
        data-variant={variant}
      >
        {showBack ? (
          <button
            type="button"
            className="dh__back"
            onClick={goBack}
            aria-label="Назад"
          >
            <img src={backIcon} alt="" aria-hidden="true" />
          </button>
        ) : (
          <div className="dh__spacer" aria-hidden="true" />
        )}

        {centerNode ? (
          <div className="dh__center">{centerNode}</div>
        ) : (
          <h1 className="dh__title" title={title} aria-live="polite">
            {title}
          </h1>
        )}

        {rightNode ? (
          <div className="dh__right">{rightNode}</div>
        ) : showMenu ? (
          <button
            type="button"
            className="dh__menu"
            onClick={onMenuClick}
            aria-label="Меню"
          >
            <img src={moreIcon} alt="" aria-hidden="true" />
          </button>
        ) : (
          <div className="dh__spacer" aria-hidden="true" />
        )}
      </header>

      {withSpacer && (
        <div
          className={[
            'dh-spacer',
            size === 'regular' ? 'dh-spacer--regular' : '',
            respectSafeAreaTop ? 'dh-spacer--safe-top' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )}
    </>
  );
}